import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useGeolocation } from '../adapters/ports.js';
import { getFollows, setFollows } from '../sdk/followStorage.js';
import { isUnderway } from '../sdk/schedule.js';
import { auctionHousesStore } from './auction-houses.store.js';
import { biddingStore } from './bidding.store.js';
import { saleIndexStore } from './sale-index.store.js';

/**
 * Sales-catalog store — backs the two consumer browse-and-follow screens:
 * "My Sales" (sales) and "My Auctions" (auction houses). These replaced the
 * old combined "Sales & auctions" directory + the followed-only "My Sales" /
 * "My Events" screens (Midnight Mint handoff: c-sales-auctions split in two).
 *
 * Both halves are real now. Sales are the live auction events the runtime serves
 * (via the bidding store's grouped lots) plus the sales the schedule holds that
 * the runtime does not serve yet; houses are records
 * from vibe-server, and what each screen used to state as data — a house's
 * distance, whether it is live, and when it next runs — is derived here from
 * the dealer's position and the sales scheduled against it. Follow state is
 * local + optimistic (no network), matching the handoff's follow toggles.
 */

// Lane-type → display chip. Falls back to the raw type for anything new.
const TYPE_LABELS = {
    Simulcast: 'SIMULCAST',
    Timed: 'TIMED ONLINE',
    Sequential: 'SEQUENTIAL',
    automatedAuction: 'AUTOMATED',
    simulcastAuction: 'SIMULCAST',
    timedAuction: 'TIMED ONLINE'
};

// Weekday + clock for a house's next scheduled sale, e.g. 'Tue 9:00 AM'.
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatNextStart = (isoTimestamp) => {
    const date = new Date(isoTimestamp);
    if (Number.isNaN(date.getTime())) return null;
    let hour = date.getHours();
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${WEEKDAY_SHORT[date.getDay()]} ${hour}:${minutes} ${meridiem}`;
};

export const salesCatalogStore = defineStore('salesCatalogStore', () => {
    const bidding = biddingStore();
    const auctionHouses = auctionHousesStore();
    const saleIndex = saleIndexStore();

    const followedSaleRefIds = reactive(new Set());
    const followedHouseIds = reactive(new Set());
    // What a v1 device stored and nothing can yet turn into an id: a sale by
    // NAME, a house by SLUG. They stay here until the sale index and the house
    // roster can say which id each one means, and they are tried again on every
    // later load — a name that names two sales is never guessed at. Nothing is
    // dropped for being unresolvable yet, which is the whole promise: a follow
    // is never lost.
    const pendingSaleNames = reactive(new Set());
    const pendingHouseSlugs = reactive(new Set());
    const loading = ref(false);
    // Where the dealer is, or null while nobody has said. Null reads as "cannot
    // place this house", which the row renders as a blank distance rather than
    // as a measurement from somewhere they have never been.
    const origin = ref(null);

    // The follows are remembered on this device (sdk/followStorage.js). They are read
    // once, and merged into whatever the dealer has followed in the meantime rather than
    // written over it. Nothing is written until the stored ones have been read, so a
    // refresh can never save an empty set over the dealer's own.
    //
    // `followsHydrated` is reactive and exported, because a surface that COUNTS
    // follows has to know whether the set it is counting has been read yet: the
    // alternative was to wait on `auctionHouses.loaded`, the roster this load
    // ends with, which says nothing about the device read and reads as a 0 when
    // some other screen filled the roster first.
    const followsHydrated = ref(false);
    let hydratingFollows = null;
    function hydrateFollows() {
        if (!hydratingFollows) {
            hydratingFollows = getFollows()
                .then(({ sales: storedSales, houses: storedHouses, pending }) => {
                    storedSales.forEach((id) => followedSaleRefIds.add(id));
                    storedHouses.forEach((id) => followedHouseIds.add(id));
                    pending.sales.forEach((name) => pendingSaleNames.add(name));
                    pending.houses.forEach((slug) => pendingHouseSlugs.add(slug));
                })
                // A device that cannot be read starts with no follows rather than failing.
                .catch(() => {})
                .finally(() => {
                    followsHydrated.value = true;
                    resolvePendingFollows();
                });
        }
        return hydratingFollows;
    }

    // Turn what is pending into ids, as far as what has loaded allows. Called
    // when the device read lands and again whenever either source answers,
    // because the index and the roster load after the device does — and a sale
    // that is not in the index TODAY can be back in it tomorrow.
    function resolvePendingFollows() {
        for (const name of [...pendingSaleNames]) {
            const saleRefId = saleIndex.saleRefIdForName(name);
            if (!saleRefId) continue;
            pendingSaleNames.delete(name);
            followedSaleRefIds.add(saleRefId);
        }
        for (const slug of [...pendingHouseSlugs]) {
            const houseId = auctionHouses.houseFor(slug)?.id;
            if (!houseId) continue;
            pendingHouseSlugs.delete(slug);
            followedHouseIds.add(houseId);
        }
    }

    watch(
        () => [saleIndex.sales.length, auctionHouses.houses.length],
        () => {
            if (!followsHydrated.value) return;
            resolvePendingFollows();
        }
    );

    watch(
        () => [[...followedSaleRefIds], [...followedHouseIds], [...pendingSaleNames], [...pendingHouseSlugs]],
        ([storedSales, storedHouses, stillPendingSales, stillPendingHouses]) => {
            if (!followsHydrated.value) return;
            void setFollows({
                sales: storedSales,
                houses: storedHouses,
                pending: { sales: stillPendingSales, houses: stillPendingHouses }
            }).catch(() => {});
        }
    );

    async function load() {
        void hydrateFollows();
        loading.value = true;
        try {
            // Independent reads, settled rather than raced to the first
            // failure: a runtime outage should empty the sales list, not the
            // house list, and a denied location permission should cost the
            // distances rather than the screen. Only `loadLots` can reject —
            // the other three swallow their own errors by construction — and
            // every caller invokes `load()` un-awaited, so a rejection here was
            // previously an unhandled promise rejection rather than anything
            // a screen could act on.
            const outcomes = await Promise.allSettled([
                bidding.loadLots(),
                auctionHouses.ensureLoaded(),
                saleIndex.ensureLoaded(),
                useGeolocation().currentPosition().then((resolved) => { origin.value = resolved ?? null; })
            ]);
            for (const outcome of outcomes) {
                if (outcome.status === 'rejected') {
                    console.warn('Sales catalog load partially failed', outcome.reason);
                }
            }
        } finally {
            loading.value = false;
        }
    }

    // The sales the runtime serves, derived from the live-event groups. A sale is
    // "live" when any of its lanes has a lot currently on the floor (non-scheduled).
    const runtimeSales = computed(() =>
        bidding.allGroups.map((group) => {
            const lots = group.lanes.flatMap((l) => l.lots);
            const live = lots.some((l) => !l.scheduled);
            const laneCount = group.lanes.length;
            return {
                // The sale's minted id, which the bidding store resolved from
                // the lane's runtime event. `title` is the name off the lot,
                // drawn and never matched.
                id: group.saleRefId,
                title: group.saleName,
                type: group.type,
                typeLabel: TYPE_LABELS[group.type] || String(group.type || '').toUpperCase(),
                live,
                laneCount,
                lotCount: lots.length,
                status: live
                    ? `Live now · ${laneCount} ${laneCount === 1 ? 'lane' : 'lanes'} · ${lots.length} ${lots.length === 1 ? 'asset' : 'assets'}`
                    : `${lots.length} ${lots.length === 1 ? 'asset' : 'assets'} scheduled`
            };
        })
    );

    // The sales the schedule holds that the runtime does not serve yet — a sale on a
    // later day has no event running, so it has no lots to group. They are sales
    // all the same: a calendar of what is coming is made of them. Each is one sale
    // however many lanes it has, never live (the runtime says what is live), and
    // counts its assets from the lanes' run lists.
    const scheduledOnlySales = computed(() => {
        const served = new Set(runtimeSales.value.map((sale) => sale.id));
        const grouped = new Map();
        for (const lane of saleIndex.sales) {
            // Keyed by the sale's minted id, so two lanes whose names differ by
            // a stray space are the one card they are, and two weeks of one
            // weekly name stay two sales. A lane with no id yet is left out
            // rather than grouped by name — there is nothing to follow it by.
            if (!lane.saleRefId || !lane.saleName || served.has(lane.saleRefId)) continue;
            if (!grouped.has(lane.saleRefId)) grouped.set(lane.saleRefId, []);
            grouped.get(lane.saleRefId).push(lane);
        }
        return [...grouped.entries()].map(([saleRefId, lanes]) => {
            const lotCount = lanes.reduce((total, lane) => total + (lane.runList?.length ?? 0), 0);
            const type = lanes[0].eventType;
            return {
                id: saleRefId,
                title: lanes[0].saleName,
                type,
                typeLabel: TYPE_LABELS[type] || String(type || '').toUpperCase(),
                live: false,
                laneCount: lanes.length,
                lotCount,
                status: `${lotCount} ${lotCount === 1 ? 'asset' : 'assets'} scheduled`
            };
        });
    });

    const sales = computed(() => [...runtimeSales.value, ...scheduledOnlySales.value]);

    // Sale documents grouped by the house they run at, by the house's own
    // document id. A sale document is one LANE, so several rows can share a
    // house — 'Lanes A–D' below is the count of them, not a guess.
    //
    // A lane carrying no house id joins no house. There is no slug fallback: an
    // administrator editing a house's slug in administration left every one of
    // that house's sales joined to nothing, which is the whole reason the lane
    // carries the house's `_id` now.
    const salesByHouseId = computed(() => {
        const grouped = new Map();
        for (const sale of saleIndex.sales) {
            const houseId = sale.auctionHouseId;
            if (!houseId) continue;
            if (!grouped.has(houseId)) grouped.set(houseId, []);
            grouped.get(houseId).push(sale);
        }
        return grouped;
    });

    // The houses each sale runs at, by the sale's minted id, each house by its
    // own document id. A sale is several lanes, and the lanes of one sale can
    // sit at more than one house, so this is a set.
    //
    // THE JOIN IS THE HOUSE'S `_id`, which is also what a followed house is
    // saved under — one value, no resolution step. It used to be the lane's
    // `auctionHouseCode`, resolved through the roster, and that slug is editable:
    // renaming a house's slug in administration dropped every one of its sales
    // off Home, out of the My Sales union and out of the menu counts, while the
    // follow itself survived.
    //
    // A lane with no house id joins no house. There is no slug fallback: a slug
    // an administrator renamed names the wrong house or none, which is exactly
    // the guess this arc exists to refuse.
    const houseIdsBySaleRefId = computed(() => {
        const grouped = new Map();
        for (const lane of saleIndex.sales) {
            if (!lane.saleRefId || !lane.auctionHouseId) continue;
            if (!grouped.has(lane.saleRefId)) grouped.set(lane.saleRefId, new Set());
            grouped.get(lane.saleRefId).add(lane.auctionHouseId);
        }
        return grouped;
    });

    // The sales the runtime currently reports as running, by id, so a house's
    // liveness comes from the same source the sales list reads rather than from
    // a second opinion.
    const liveSaleRefIds = computed(
        () => new Set(sales.value.filter((sale) => sale.live).map((sale) => sale.id))
    );

    /**
     * A house, with the three things the browse row states about it derived
     * rather than stored: how far away it is, whether it is running anything,
     * and when it next does.
     */
    const houses = computed(() =>
        auctionHouses.houses.map((house) => {
            const scheduled = salesByHouseId.value.get(house.id) ?? [];
            const laneCount = scheduled.length;
            // The runtime's answer when it has one, the schedule's otherwise.
            //
            // The schedule half is not a nicety: `startAt: null` means RUNNING
            // NOW, and the earlier version filtered those out before looking for
            // the earliest start — so a house holding three live lanes reported
            // that it had nothing scheduled, which is what the screenshots
            // caught.
            const live = liveSaleRefIds.value.size
                ? scheduled.some((sale) => liveSaleRefIds.value.has(sale.saleRefId))
                : scheduled.some((sale) => isUnderway(sale.startAt));
            const nextStart = scheduled
                .map((sale) => sale.startAt)
                .filter(Boolean)
                .sort()[0] ?? null;
            // The sale the row's last line is about, so the row can open it:
            // the one running there now, else the one that starts next. Null
            // for a house with nothing scheduled, which then has nothing to
            // open. Lanes of one sale share its id, so any live lane names it.
            const liveSale = live
                ? scheduled.find((sale) => (liveSaleRefIds.value.size
                    ? liveSaleRefIds.value.has(sale.saleRefId)
                    : isUnderway(sale.startAt)))
                : null;
            const nextSale = nextStart ? scheduled.find((sale) => sale.startAt === nextStart) : null;
            const rowSale = liveSale ?? nextSale ?? null;
            // The id opens it; the name is what the row says it is opening.
            const saleRefId = rowSale?.saleRefId ?? null;
            const saleName = rowSale?.saleName ?? null;
            const distanceMiles = auctionHouses.distanceFrom(house.code, origin.value);
            return {
                // The house's Mongo `_id`. A follow keys by it: the slug is the
                // join to a sale and an item, not something to save under.
                id: house.id,
                // The slug. Kept beside the id because an item carries it in
                // AUCTIONID and the roster measures a distance by it. No sale
                // joins a house by it any more.
                code: house.code,
                name: house.name,
                loc: [house.city, house.state].filter(Boolean).join(', '),
                distanceMiles,
                // An un-placeable house reads as blank rather than as
                // 'Infinity mi'. It can happen — a house seeded without
                // coordinates — and a blank row is honest where a number is not.
                dist: Number.isFinite(distanceMiles) ? `${Math.round(distanceMiles)} mi` : null,
                live,
                saleRefId,
                saleName,
                // 'No sales scheduled' is reachable only when the house really
                // holds none: a lane with a null start reads live above, and a
                // lane with a future start has a nextStart to name.
                when: live
                    ? `Live now · ${laneCount} ${laneCount === 1 ? 'lane' : 'lanes'}`
                    : (nextStart ? formatNextStart(nextStart) : 'No sales scheduled')
            };
        })
    );

    const isSaleFollowed = (id) => followedSaleRefIds.has(id);
    const isHouseFollowed = (id) => followedHouseIds.has(id);
    // A sale "in My Auctions" runs, on any lane, at a house the dealer follows.
    // Asked by the sale's minted id, like every other follow question.
    const isSaleInFollowedHouse = (id) => {
        for (const houseId of houseIdsBySaleRefId.value.get(id) ?? []) {
            if (followedHouseIds.has(houseId)) return true;
        }
        return false;
    };

    // Does the dealer follow anything at all — a sale or a house? Home's sections read
    // it to tell "you follow nothing yet" from "nothing of yours is in this window".
    // Pending counts: a dealer whose follow has not resolved yet still follows
    // something, and "you follow nothing yet" would be a lie to them.
    const hasFollows = computed(() => followedSaleRefIds.size > 0
        || followedHouseIds.size > 0
        || pendingSaleNames.size > 0
        || pendingHouseSlugs.size > 0);

    function toggleSale(id) {
        if (followedSaleRefIds.has(id)) followedSaleRefIds.delete(id);
        else followedSaleRefIds.add(id);
    }
    function toggleHouse(id) {
        if (followedHouseIds.has(id)) followedHouseIds.delete(id);
        else followedHouseIds.add(id);
    }

    // "My Auctions" browse list — live first, then nearest. Compared rather
    // than subtracted: two un-placeable houses are both Infinity, and
    // `Infinity - Infinity` is NaN, which makes the comparator inconsistent and
    // the order arbitrary. This sorts them last and keeps them stable.
    const auctions = computed(() =>
        [...houses.value].sort(
            (a, b) =>
                Number(b.live) - Number(a.live) ||
                (a.distanceMiles === b.distanceMiles ? 0 : (a.distanceMiles < b.distanceMiles ? -1 : 1))
        )
    );

    return {
        loading,
        // Have the follows stored on this device been read? A count over the
        // follow set is `null` rather than 0 until they have.
        followsHydrated,
        // What a v1 device stored that nothing can resolve yet. Exposed so the
        // promise is inspectable: these are follows, held, not lost.
        pendingFollows: computed(() => ({ sales: [...pendingSaleNames], houses: [...pendingHouseSlugs] })),
        sales, houses, auctions,
        isSaleFollowed, isHouseFollowed, isSaleInFollowedHouse, hasFollows, toggleSale, toggleHouse,
        load
    };
});
